import { FALLBACK_MAP_CENTER, LatLngLiteral, SettingNode, SettingType, SettingValue } from '@cco/model';
import { Dictionary } from '@ngrx/entity';
import { createFeatureSelector, createSelector, select } from '@ngrx/store';
import { cloneDeep } from 'lodash';
import * as fromGlobalSelect from '@app/store/global-select.reducer';
import { EntitySelectorsFactory } from '@ngrx/data';
import { State } from '@app/store/reducers';
import { Observable } from 'rxjs';
import { distinctUntilChanged, filter, map } from 'rxjs/operators';
import { Duration } from 'luxon';
import { roundingMethodFromRoundingMode } from '@app/shared/utils/math-utils';

function buildSettingTree(nodes: SettingNode[], settings: SettingNode[]): SettingNode[] {
  const newNodes: SettingNode[] = [];
  nodes.forEach((node) => {
    const newNode = typeof structuredClone === 'function' ? structuredClone(node) : cloneDeep(node);
    const children = settings.filter(
      setting => setting.parentId === node.id || (!setting.parentId && !node.id),
    );
    if (children) {
      newNodes.push({ ...newNode, children: buildSettingTree(children, settings) });
    } else {
      newNodes.push({ ...newNode, children: [] });
    }
  });

  return newNodes;
}

const entitySelectorFactory = new EntitySelectorsFactory();

export const settingTypeSelectors = entitySelectorFactory.create<SettingType>('SettingType');
export const settingNodeSelectors = entitySelectorFactory.create<SettingNode>('SettingNode');
export const resolvedSettingSelectors = entitySelectorFactory.create<SettingValue>('ResolvedSetting');

export const selectSettingTypes = settingTypeSelectors.selectEntities;
export const selectSettingTypeEntityMap = settingTypeSelectors.selectEntityMap;

export const selectSettingNodes = settingNodeSelectors.selectEntities;
export const selectSettingNodeEntityMap = settingNodeSelectors.selectEntityMap;

const selectSelectsState = createFeatureSelector<fromGlobalSelect.State>('selects');
const selectSelectedEntityId = (entityName: string) =>
  createSelector(selectSelectsState, state => state[entityName]);
export const selectSelectedSettingNodeId = selectSelectedEntityId('SettingNode');

export const selectSelectedSettingNode = createSelector(
  selectSettingNodeEntityMap,
  selectSelectedSettingNodeId,
  (entityMap, id) => entityMap?.[id]);

export const selectSelectedSettingNodeName = createSelector(
  selectSelectedSettingNode,
  settingNode => settingNode?.name);

/**
 * props.path is the path to the parent node of the desired subtree
 */
export const selectSettingSubtree = (props?: { path: string[]; excludeParent?: boolean }) => createSelector(
  selectSettingNodes,
  (settings: SettingNode[]) => {
    let parentNode: SettingNode;

    if (props && props.path) {
      let node: SettingNode;
      for (let i = 0; i < props.path.length; i++) {
        if (i === 0) {
          node = settings.find(
            setting => setting.parentId == null && setting.pathSegment === props.path[0],
          );
        } else {
          node = settings.find(
            setting =>
              setting.parentId === parentNode.id && setting.pathSegment === props.path[i],
          );
        }

        if (!node || !node.id) {
          return null;
        }

        parentNode = node;
      }
    } else {
      parentNode = { id: null } as SettingNode;
    }

    const settingTree = buildSettingTree([parentNode], settings);

    if (settingTree.length === 1) {
      if (settingTree[0].id == null || props.excludeParent) {
        if (!settingTree[0].children || settingTree[0].children.length === 0) {
          return null;
        }

        return settingTree[0].children;
      }
    }

    return settingTree;
  },
);
export const selectSettingNodeByPath = (path: string[]) => createSelector(
  selectSettingNodes,
  (settings: SettingNode[]) => {
    let node: SettingNode;
    for (let i = 0; i < path.length; i++) {
      if (i === 0) {
        node = settings.find(
          setting =>
            setting.parentId == null &&
            setting.pathSegment === path[0],
        );
      } else {
        node = settings.find(
          setting => setting.parentId === node.id && setting.pathSegment === path[i],
        );
      }

      if (!node) {
        return null;
      }
    }

    return node;
  },
);
export const selectSettingNodeIdByPath = (path: string[]) => createSelector(
  selectSettingNodeByPath(path),
  (settingNode: SettingNode) => {
    return settingNode?.id;
  },
);

export const selectResolvedSettingEntityMap = resolvedSettingSelectors.selectEntityMap;
export const selectResolvedSettingBySettingId = (settingId: number) => createSelector(
  selectResolvedSettingEntityMap,
  (entities: Dictionary<SettingValue>) => {
    return entities && entities[settingId];
  },
);
export const selectResolvedSettingByPath = (path: string[]) => createSelector(
  selectResolvedSettingEntityMap,
  selectSettingNodeIdByPath(path),
  (entities, settingId) => {
    return entities && entities[settingId];
  },
);
export const selectResolvedSettingByPathAsNumber = (path: string[]) => (source: Observable<State>) => source.pipe(
  select(selectResolvedSettingByPath(path)),
  distinctUntilChanged(),
  map(setting => setting?.value as number),
);
export const selectResolvedSettingByPathAsBoolean = (path: string[]) => (source: Observable<State>) => source.pipe(
  select(selectResolvedSettingByPath(path)),
  distinctUntilChanged(),
  map(setting => setting?.value as boolean),
);
export const selectResolvedSettingByPathAsString = (path: string[]) => (source: Observable<State>) => source.pipe(
  select(selectResolvedSettingByPath(path)),
  distinctUntilChanged(),
  map(setting => setting?.value as string),
);
export const selectResolvedSettingBySettingIdAsNumber = (id: number) => (source: Observable<State>) => source.pipe(
  select(selectResolvedSettingBySettingId(id)),
  distinctUntilChanged(),
  filter(value => !!value),
  map(setting => setting?.value as number),
);
export const selectResolvedSettingBySettingIdAsRoundingMethod = (id: number) => (source: Observable<State>) => source.pipe(
  select(selectResolvedSettingBySettingId(id)),
  distinctUntilChanged(),
  filter(value => !!value),
  map(setting => roundingMethodFromRoundingMode(setting?.value as string)),
);
export const selectResolvedSettingBySettingIdAsDuration = (id: number) => (source: Observable<State>) => source.pipe(
  select(selectResolvedSettingBySettingId(id)),
  distinctUntilChanged(),
  filter(value => !!value),
  map(setting => Duration.fromISO(setting.value as string)),
  filter(margin => margin.isValid)
);
export const selectGoogleMapTypeIdResolvedSettingByPath = (path: string[]) =>
  (source: Observable<State>) => source.pipe(
    selectResolvedSettingByPathAsString(path),
    map(defaultMapType => defaultMapType ?? 'hybrid'),
  );
export const selectCoordsResolvedSettingByPath = (path: string[]) =>
  (source: Observable<State>) => source.pipe(
    select(selectResolvedSettingByPath(path)),
    distinctUntilChanged(),
    map(setting => setting?.value as LatLngLiteral),
    map(coords => coords && coords.lat && coords.lng && coords || FALLBACK_MAP_CENTER),
  );
export const selectGeofenceTypeResolvedSettingByPath = (path: string[]) =>
  (source: Observable<State>) => source.pipe(
    selectResolvedSettingByPathAsString(path),
    map(defaultGeofenceTypeStr => defaultGeofenceTypeStr === 'polygon' ? 'Polygon'
      : defaultGeofenceTypeStr === 'circle' ? 'Circle'
        : 'Polygon'),
  );
